import { ROUTER_PATHS, getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import { IFromProspecto } from '@/shared/interfaces/erp/prospecto';
import { IDataProspectoPymesParams, IProspectoPymes, IProspectoPymesResp } from '@/shared/interfaces/erp/ventas_pymes/prospecto_pymes.interface';
import { useUiStore } from '@/store/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const { get, post, put } = erpApi();

export type CreateProspectoProps = Omit<IProspectoPymes, 'id_prospecto_pymes'>;

export const useGetProspectoPymes = (params: IDataProspectoPymesParams, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['prospectopymes', ...Object.values(params || {})],
    queryFn: async () => {
      return await getProspectoPymes(params);
    },
    retry: false,
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const getProspectoPymes = async (params?: IDataProspectoPymesParams) => {
  const queryParams = getUrlParamsFetch(params || {});
  const resp = get<IProspectoPymesResp>(
    `${urlBackend}api/v1/prospectopymes/?${queryParams}`,
    true
  );
  return resp;
};

export const useGetProspectoByIdProspectoPymes = (id: string) => {
  return useQuery({
    queryKey: ['prospectopymes', id],
    queryFn: async () => {
      return getProspectoByIdProspectoPymes(id);
    },
    refetchOnWindowFocus: false,

  });
};

export const getProspectoByIdProspectoPymes = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<IProspectoPymes>(`${urlBackend}api/v1/prospectopymes/${id}`, true);
  res.status === 'error' && setAlert(true, res.message, 'error');

  return res.data;
};

export const usePostProspectoPymes = ({
  navigate,
  returnUrl = ROUTER_PATHS.ventas.preventasCrearNav
}: UseMutationsParams) => {
  const queryClient = useQueryClient();

  const setAlert = useUiStore.getState().setAlert;
  const setConfirmDialog = useUiStore.getState().setConfirmDialog;
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;


  return useMutation({

    mutationFn: postProspectoPymes,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['prospectopymes'] });
      if (data.code == 200) {
        setConfirmDialogIsOpen(true);


        setConfirmDialog({
          isOpen: true,
          title: '¡Prospecto creado con éxito!',
          subtitle:
            'El prospecto se ha creado exitosamente. El siguiente paso es proceder con la creación de una preventa. ¿Deseas continuar?',
          onClose() {
            window.location.reload();
          },
          onConfirm() {
            setConfirmDialogIsOpen(false);
            navigate && navigate(`${returnUrl}/${data.data.id_prospecto_pymes}`);
          },
          cancelTextBtn: 'Cerrar',
        });
        return;
      }

      setAlert(true, data.message, data.status);
    },

    onError: (e => {
      setIsGlobalLoading(false);
      handleAxiosError(e);
    }),
  });
};

export const postProspectoPymes = async (data: IFromProspecto) => {
  const res = await post<IProspectoPymes>(`${urlBackend}api/v1/prospectopymes/`, data, true);
  return res;
};

export const usePutProspectoPymes = ({
  enableAlert = false
}: UseMutationsParams) => {

  const setAlert = useUiStore.getState().setAlert;

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: IDataProspectoPymesParams;
    }) => {
      return putProspectoPymes({ data: data, id: id });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['prospectopymes'] });

      enableAlert && setAlert(true, data.message, AlertSeverityEnum.success)
    },
    onError: (error) => {
      handleAxiosError(error);
    }
  });
};

export const putProspectoPymes = async (
  { id, data }: {
    id: string,
    data: IDataProspectoPymesParams
  }
) => {
  const res = await put<IProspectoPymes>(`${urlBackend}api/v1/prospectopymes/${id}/`, data, true);
  return res;
};
